import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "../lib/router"
import { Clock3, Shield, RefreshCw } from "lucide-react"
import { useLanguage } from "../lib/LanguageContext"
import LangToggle from "../components/LangToggle"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function VerificationPending() {
  const navigate = useNavigate()
  const { t } = useLanguage()

  const name = localStorage.getItem("name") || "there"

  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleSignOut = () => {
    localStorage.removeItem("token")
    localStorage.removeItem("role")
    localStorage.removeItem("name")
    localStorage.removeItem("clubName")
    navigate("/login")
  }

  const checkStatus = useCallback(async () => {
    const token = localStorage.getItem("token")
    if (!token) return
    setIsRefreshing(true)
    try {
      const res = await fetch(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        if (data.status === "approved") {
          navigate("/dashboard")
        }
      }
    } catch (e) {
      // Ignore network errors
    } finally {
      setIsRefreshing(false)
    }
  }, [navigate])

  useEffect(() => {
    checkStatus()
  }, [checkStatus])

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-900 px-4 py-10">
      <div
        className="w-full max-w-xl overflow-hidden border border-white/10 bg-ink-800/60 p-8 shadow-2xl backdrop-blur-sm"
        style={{ boxShadow: "0 0 0 1px rgba(217,179,108,.06), 0 32px 64px -16px rgba(0,0,0,.6)" }}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px]" style={{ background: "linear-gradient(90deg, transparent, #D9B36C 50%, transparent)" }} aria-hidden="true" />

        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full border border-gold-500/40 bg-gold-500/10 text-gold-400">
            <Clock3 className="h-8 w-8" aria-hidden="true" />
          </div>
        </div>

        <div className="text-center">
          <p className="mb-2 text-xs font-bold uppercase tracking-[.18em] text-gold-400">
            {t("vp_subtitle")}
          </p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">
            {t("vp_title")}
          </h1>
        </div>

        <div className="mt-6 rounded border border-gold-500/30 bg-gold-500/10 p-4 text-sm text-gold-200">
          <p className="font-medium">
            Hello {name}, {t("vp_body")}
          </p>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 rounded border border-white/10 bg-ink-700/40 px-4 py-3 text-sm text-mist-200">
          <Shield className="h-4 w-4 text-mist-300" aria-hidden="true" />
          <span>Current status: {t("coach_pending")}</span>
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={checkStatus}
            disabled={isRefreshing}
            className="flex h-11 flex-1 items-center justify-center gap-2 rounded bg-gold-500 px-4 text-sm font-semibold text-ink-900 transition-colors hover:bg-gold-400 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh status
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex h-11 flex-1 items-center justify-center border border-white/20 bg-transparent px-4 text-sm font-semibold text-paper-0 transition-colors hover:border-white/40 hover:bg-white/5"
          >
            {t("vp_sign_out")}
          </button>
        </div>
      </div>
      <div className="mt-6 flex justify-center">
        <LangToggle />
      </div>
    </div>
  )
}
